import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { componentStyles } from '@/lib/theme'
import useIsMobile from '@/hooks/useIsMobile'

interface ForgotPasswordFormProps {
  error: string | null
  success: string | null
  isLoading: boolean
  onSubmit: (form: HTMLFormElement) => void
  onBack?: () => void
}

export default function ForgotPasswordForm({
  error,
  success,
  isLoading,
  onSubmit,
  onBack,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation()
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()

  // Mobile styles - keep comfortable sizes for touch
  const mobileStyles = isMobile ? {
    heading: { fontSize: '13px', marginBottom: '12px' },
    label: { fontSize: '12px', minWidth: '50px' },
    input: { fontSize: '14px', padding: '10px 12px' },
    button: { fontSize: '12px', padding: '10px 14px' },
    message: { fontSize: '11px' },
    formRow: { gap: '8px' },
  } : {}

  return (
    <>
      <p style={{ 
        ...componentStyles.authWindow.heading, 
        color: colors.secondaryText,
        ...mobileStyles.heading,
      }}>
        {t('auth.forgotPassword.description')}
      </p>
      <form style={{ ...componentStyles.authWindow.form, gap: isMobile ? '12px' : '18px' }}>
        <div style={{ ...componentStyles.authWindow.formRow, ...mobileStyles.formRow }}>
          <label htmlFor="email" style={{ 
            ...componentStyles.authWindow.label, 
            color: colors.primaryText,
            ...mobileStyles.label,
          }}>
            {t('auth.forgotPassword.email')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            style={{
              ...componentStyles.authWindow.input,
              border: `1px solid ${colors.primaryBorder}`,
              background: colors.primaryBg,
              color: colors.primaryText,
              ...mobileStyles.input,
            }}
          />
        </div>
        {(error || success) && (
          <div style={{
            ...componentStyles.authWindow.messageContainer,
            marginTop: '4px',
            marginBottom: '8px',
          }}>
            {error && (
              <p style={{ 
                ...componentStyles.authWindow.errorMessage, 
                color: colors.errorBorder,
                ...mobileStyles.message,
              }}>{error}</p>
            )}
            {success && (
              <p style={{ 
                ...componentStyles.authWindow.successMessage, 
                color: colors.successBorder,
                ...mobileStyles.message,
              }}>{success}</p>
            )}
          </div>
        )}
        <div style={{ ...componentStyles.authWindow.buttonContainer, ...(isMobile && { gap: '8px' }) }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{
                ...componentStyles.authWindow.button,
                background: colors.secondaryBg,
                color: colors.primaryText,
                border: `1px solid ${colors.primaryBorder}`,
                ...mobileStyles.button,
              }}
            >
              {t('app.back')}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              const form = e.currentTarget.closest('form')
              if (form) onSubmit(form)
            }}
            disabled={isLoading}
            style={isLoading ? {
              ...componentStyles.authWindow.buttonLoading,
              background: colors.secondaryBg,
              color: colors.tertiaryText,
              border: `1px solid ${colors.primaryBorder}`,
              ...mobileStyles.button,
            } : {
              ...componentStyles.authWindow.button,
              background: colors.secondaryBg,
              color: colors.primaryText,
              border: `1px solid ${colors.primaryBorder}`,
              ...mobileStyles.button,
            }}
          >
            {isLoading ? t('auth.processing') : t('auth.forgotPassword.submit')}
          </button>
        </div>
      </form>
    </>
  )
}
